import React from 'react';

import { Badge, StatusBadge, type BadgeStatus } from '@/design-system/badge';
import type { UjpAmendmentStatus, UjpStatus } from '@/services/api/ujp';

import { UJP_COPY } from '../copy';
import { ageDays, formatAge } from '../format';

/**
 * UJP status → kit status tone (DD3): SUBMITTED is the live, waiting state
 * (away + pulsing dot), APPROVED success, REJECTED error, CANCELLED neutral.
 * Shared by the list column and the panel header so the two never disagree.
 */
export const UJP_STATUS_TONE: Record<UjpStatus, BadgeStatus> = {
  SUBMITTED: 'away',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

export const UjpStatusBadge: React.FC<{
  status: UjpStatus;
  size?: 'sm' | 'md';
}> = ({ status, size = 'sm' }) => (
  <StatusBadge
    status={UJP_STATUS_TONE[status] ?? 'neutral'}
    variant="dot-light"
    size={size}
    pulse={status === 'SUBMITTED'}
  >
    {UJP_COPY.status[status] ?? status}
  </StatusBadge>
);

/**
 * Biaya tambahan status (CR-7) — the same tones as the UJP badge so a pending
 * amendment reads like a pending UJP: waiting (away, pulsing) / success / error.
 */
export const UJP_AMENDMENT_STATUS_TONE: Record<UjpAmendmentStatus, BadgeStatus> = {
  SUBMITTED: 'away',
  APPROVED: 'success',
  REJECTED: 'error',
};

export const UjpAmendmentStatusBadge: React.FC<{
  status: UjpAmendmentStatus;
  size?: 'sm' | 'md';
}> = ({ status, size = 'sm' }) => (
  <StatusBadge
    status={UJP_AMENDMENT_STATUS_TONE[status] ?? 'neutral'}
    variant="dot-light"
    size={size}
    pulse={status === 'SUBMITTED'}
  >
    {UJP_COPY.amendment.status[status] ?? status}
  </StatusBadge>
);

/** Aging chip (DD6): amber past 2 days, red past 5, neutral before. */
export const UjpAgeBadge: React.FC<{ createdAt: string; now?: Date }> = ({
  createdAt,
  now,
}) => {
  const days = ageDays(createdAt, now);
  const status: BadgeStatus =
    days > 5 ? 'error' : days > 2 ? 'away' : 'neutral';
  return (
    <Badge status={status} className="tabular-nums whitespace-nowrap">
      {formatAge(createdAt, now)}
    </Badge>
  );
};
